import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { z } from 'zod'
import { Button } from '@/src/components/ui/button'
import { Input } from '@/src/components/ui/input'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { DataTable, type ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { listFiles, deleteFile } from '@/src/api/file'
import { ShareLinkCell } from '@/src/components/manage/tables/CopyableCell'
import { IsometricIcon } from '@/src/components/manage/tables/IsometricIcon'
import { Badge } from '@/src/components/ui/badge'
import { Pagination } from '@/src/components/manage/Pagination'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/src/components/ui/select'
import { ArrowCounterClockwiseIcon, TrashIcon } from '@phosphor-icons/react'
import { useTranslation } from '@/src/i18n/client'
import { ViewAction } from '@/src/components/manage/actions/ViewAction'
import { ReanalyzeAction } from '@/src/components/manage/actions/ReanalyzeAction'
import { DeleteAction } from '@/src/components/manage/actions/DeleteAction'
import { FilterAction } from '@/src/components/manage/actions/FilterAction'
import { FilePreviewPopover } from '@/src/components/file/FilePreviewPopover'
import { DateTimeText } from '@/src/components/ui/date-time'
import { useAuth } from '@/src/components/AuthContextProvider'
import { toast } from 'sonner'
import { ConfirmModal } from '@/src/components/ui/confirm-modal'

const PAGE_SIZE = 20

const searchSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
})

export const Route = createFileRoute('/_manage/manage/files/')({
  component: UserFilesPage,
  validateSearch: searchSchema,
})

function UserFilesPage() {
  const { t } = useTranslation()
  const { user, isAdmin } = useAuth()
  const searchParams = Route.useSearch()
  const navigate = Route.useNavigate()
  const [search, setSearch] = useState('')
  const [projectId, setProjectId] = useState('')
  const page = (searchParams.page ?? 1) - 1
  const setPage = (nextPage: number) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        page: nextPage > 0 ? nextPage + 1 : undefined,
      }),
    })
  }
  const [searchType, setSearchType] = useState<'project' | 'search'>('search')
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'createdAt',
    direction: 'desc',
  })
  const [hoveredFile, setHoveredFile] = useState<{ fileId: string; thumbnailKey: string | null; position: { x: number; y: number } } | null>(null)
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['file', 'list', { search, projectId, page, sort }],
    queryFn: () => listFiles({
      data: {
        search: search || undefined,
        projectId: projectId || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        orderBy: sort.key,
        order: sort.direction,
      },
    }),
  })

  const deleteMutation = useMutation({
    mutationFn: (shareId: string) => deleteFile({ data: { shareId } }),
    onSuccess: () => {
      refetch()
    },
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: async (shareIds: string[]) => {
      const results = await Promise.allSettled(
        shareIds.map(shareId => deleteFile({ data: { shareId } }))
      )
      const failed = results.filter(r => r.status === 'rejected').length
      if (failed > 0) throw new Error(`Failed to delete ${failed} of ${shareIds.length} items`)
      return results
    },
    onSuccess: (_, shareIds) => {
      toast.success(t('table.bulkDeleteSuccess', { count: shareIds.length }))
      setSelectedItems([])
      setShowBulkDeleteConfirm(false)
      refetch()
    },
    onError: () => {
      toast.error(t('table.bulkDeleteFailed'))
    },
  })

  const files = data?.files ?? []
  const total = data?.total ?? 0
  const pageCount = Math.ceil(total / PAGE_SIZE)

  // Clear selection when page or filters change
  if (selectedItems.length > 0) {
    const validIds = new Set(files.map(f => f.id))
    const invalidSelection = selectedItems.some(id => !validIds.has(id))
    if (invalidSelection) {
      setSelectedItems([])
    }
  }

  const columns: ColumnConfig<any>[] = [
    {
      key: 'shareId',
      label: t('table.shareId'),
      width: 'w-24',
      sortable: true,
      render: (file: any) => {
        const canRotate = isAdmin || (!!file.user?.id && file.user.id === user?.id)
        return <ShareLinkCell shareId={file.shareId} canRotate={canRotate} />
      }
    },
    {
      key: 'isometric',
      label: '',
      width: 'w-10',
      render: (file: any) => {
        if (!file.id) return null;
        return <IsometricIcon fileId={file.id} />;
      },
    },
    {
      key: 'name',
      label: t('table.name'),
      width: 'w-[20%]',
      sortable: true,
      primary: true,
      render: (file: any) => (
        <div
          className="relative inline-flex items-center group"
          onMouseEnter={(e) => {
            const rect = e.currentTarget.getBoundingClientRect()
            setHoveredFile({
              fileId: file.id,
              thumbnailKey: file.thumbnailLargeUrl,
              position: { x: rect.right + 8, y: rect.top }
            })
          }}
          onMouseLeave={() => {
            setHoveredFile(null)
          }}
        >
          <div className="max-w-45 truncate" title={file.name}>
            {file.name}
          </div>
        </div>
      )
    },
    {
      key: 'projectId',
      label: t('table.project'),
      width: 'w-[10%]'
    },
    {
      key: 'drawings',
      label: t('table.drawings'),
      width: 'w-2',
      align: 'center',
      render: (file) => {
        const count = Array.isArray(file.drawingIds) ? file.drawingIds.length : 0
        return <Badge variant={"secondary"}>{count ?? 0}</Badge>
      },
    },
    {
      key: 'privacy',
      label: t('table.privacy'),
      width: 'w-[10%]',
      sortable: true,
      render: (file: any) => (
        <Badge
          variant={
            file.privacy === "LOW"
              ? "default"
              : file.privacy === "MEDIUM"
                ? "secondary"
                : "outline"
          }
          className={
            file.privacy === "LOW"
              ? "px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border-green-200"
              : file.privacy === "MEDIUM"
                ? "px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border-yellow-200"
                : "px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border-red-200"
          }
        >
          {file.privacy}
        </Badge>
      )
    },
    {
      key: 'createdAt',
      label: t('table.date'),
      width: 'w-[12%]',
      sortable: true,
      render: (file: any) => <DateTimeText>{file.createdAt}</DateTimeText>
    }
  ]

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar selectedCount={selectedItems.length}>
        <AdminToolbar.BulkActions>
          <Button
            variant="destructive"
            size="lg"
            onClick={() => setShowBulkDeleteConfirm(true)}
            disabled={bulkDeleteMutation.isPending}
          >
            <TrashIcon className="w-4 h-4 mr-2" />
            {t('table.bulkDelete')}
          </Button>
        </AdminToolbar.BulkActions>
        <AdminToolbar.Right>
          <FilterAction>
            <div className="flex gap-2 items-center">
              <Select
                value={projectId ? 'project' : (search ? 'search' : undefined)}
                onValueChange={val => {
                  if (val === 'project') {
                    setSearchType('project')
                    setProjectId('')
                    setSearch('')
                  } else if (val === 'search') {
                    setSearchType('search')
                    setProjectId('')
                    setSearch('')
                  }
                  setPage(0)
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Search or Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="search">Search</SelectItem>
                  <SelectItem value="project">Filter Project</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder={searchType === 'search' ? 'Search files' : 'Project ID'}
                value={projectId || search}
                onChange={e => {
                  if (searchType === 'project') {
                    setProjectId(e.target.value)
                  } else {
                    setSearch(e.target.value)
                  }
                  setPage(0)
                }}
                className="w-40"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setSearch('')
                  setProjectId('')
                  setPage(0)
                }}
                aria-label="Reset"
              >
                <ArrowCounterClockwiseIcon className="w-4 h-4" />
              </Button>
            </div>
          </FilterAction>
          <Pagination
            page={page}
            totalPages={pageCount}
            onPageChange={setPage}
            compact
          />
        </AdminToolbar.Right>
      </AdminToolbar>
      <div className="mt-4">
        <DataTable
          data={files}
          isLoading={isLoading}
          error={error instanceof Error ? error : null}
          columns={columns}
          sort={sort}
          onSortChange={(next) => {
            setSort(next ?? { key: 'createdAt', direction: 'desc' })
            setPage(0)
          }}
          selectable={true}
          selectedItems={selectedItems}
          onSelectionChange={setSelectedItems}
          actions={(file: any) => (
            <>
              <ViewAction
                to={`/manage/files/${file.shareId}`}
              />
              <ReanalyzeAction
                file={{
                  id: file.id,
                  shareId: file.shareId,
                  name: file.name,
                  drawingIds: file.drawingIds,
                }}
              />
              <DeleteAction
                itemName={file.name}
                itemType="file"
                onDelete={() => deleteMutation.mutateAsync(file.shareId).then(() => { })}
                isPending={deleteMutation.isPending}
              />
            </>
          )}
        />
      </div>
      <FilePreviewPopover
        fileId={hoveredFile?.fileId ?? ''}
        thumbnailKey={hoveredFile?.thumbnailKey ?? null}
        position={hoveredFile?.position ?? { x: 0, y: 0 }}
        isVisible={!!hoveredFile}
        onClose={() => setHoveredFile(null)}
      />
      <ConfirmModal
        open={showBulkDeleteConfirm}
        onOpenChange={setShowBulkDeleteConfirm}
        title={t('table.bulkDeleteConfirmTitle', { count: selectedItems.length })}
        description={t('table.bulkDeleteConfirmDescription', { count: selectedItems.length })}
        confirmLabel={t('action.delete')}
        cancelLabel={t('action.cancel')}
        loadingLabel={t('action.deleting')}
        variant="destructive"
        onConfirm={() => {
          const selectedIds = new Set(selectedItems)
          const selectedFiles = files.filter(f => selectedIds.has(f.id))
          const shareIds = selectedFiles.map((f: any) => f.shareId).filter(Boolean)
          bulkDeleteMutation.mutate(shareIds)
        }}
        isLoading={bulkDeleteMutation.isPending}
      />
    </div>
  )
}
